import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import logo from '../assets/logo.png';

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.12)",
  accentBorder: "rgba(12,105,200,0.20)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgAlt: "#111111",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgAlt: "#EDF0F5",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
};

const sections = [
  {
    num: "01",
    title: "Introduction",
    body: "This Copyright Policy outlines the procedures and guidelines for addressing copyright-related matters on the Snows ProAm mobile application (the \"App\"). Snows ProAm Inc. (\"we,\" \"us,\" or \"our\") respects the intellectual property rights of others and expects our users to do the same.",
  },
  {
    num: "02",
    title: "Copyright Ownership",
    body: "All content and materials available on the App, including but not limited to text, images, graphics, audio, videos, software, and any other intellectual property, are protected by copyright laws and are the property of Snows ProAm Inc. or its content suppliers.",
  },
  {
    num: "03",
    title: "Reporting Copyright Infringements",
    body: "We take copyright infringement seriously and strive to respect the intellectual property rights of others. If you believe that your copyrighted work has been used or displayed on the App in a way that constitutes copyright infringement, please provide us with the following information:",
    list: [
      "A description of the copyrighted work you believe has been infringed.",
      "A description of the content you believe to be infringing and its location on the App.",
      "Your contact information, including your name, address, telephone number, and email address.",
      "A statement by you that you have a good-faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law.",
      "A statement by you, made under penalty of perjury, that the above information in your notice is accurate and that you are the copyright owner or authorized to act on the copyright owner's behalf.",
    ],
    note: "Please send this information to info@snowsproam.com so that we may forward it to our Designated Copyright Agent.",
  },
  {
    num: "04",
    title: "Action on Copyright Infringement Reports",
    body: "Upon receiving a proper notice of copyright infringement, we will take appropriate actions, which may include:",
    list: [
      "Removing or disabling access to the allegedly infringing content.",
      "Notifying the user who posted the content about the complaint.",
      "Termination of user accounts or access for repeat infringers.",
    ],
  },
  {
    num: "05",
    title: "Counter-Notification",
    body: "If a user believes their content was removed or disabled as a result of an incorrect copyright infringement report, they may send a counter-notification to our Designated Copyright Agent. The counter-notification should include:",
    list: [
      "Identification of the content that was removed or disabled and its location.",
      "User's contact information, including their name, address, telephone number, and email address.",
      "A statement under penalty of perjury that the user has a good-faith belief that the content was removed or disabled as a result of a mistake or misidentification.",
      "A statement that the user consents to the jurisdiction of the Irish courts and that they will accept service of process from the person who provided the original copyright infringement report.",
    ],
    note: "Please send the counter-notification to our Designated Copyright Agent at info@snowsproam.com.",
  },
  {
    num: "06",
    title: "Changes to this Copyright Policy",
    body: "We may update this Copyright Policy to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify users of any material changes by posting the updated Copyright Policy on our website or within the App.",
  },
];

const CopyrightPolicy = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .section-card { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .section-card:hover { border-color: ${C.accentBorder} !important; box-shadow: ${C.shadowMd} !important; }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
      `}</style>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 64, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" style={{
            fontSize: 13.5, fontWeight: 500, color: C.textSecondary,
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${C.border}`, background: C.bgElevated,
          }}>← Back</a>
          <button className="theme-toggle" onClick={toggleTheme}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.bgElevated, border: `1px solid ${C.border}`,
              color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "64px 24px 48px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          fontSize: 12, fontWeight: 600, color: C.accent,
          textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 20,
        }}>Legal</div>

        <h1 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(36px, 6vw, 56px)",
          fontWeight: 800, letterSpacing: -1.2, lineHeight: 1.05,
          marginBottom: 16, color: C.text,
        }}>
          Copyright <span className="grad-text">Policy</span>
        </h1>

        <p style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, maxWidth: 520 }}>
          We respect intellectual property rights and expect the same from our users. Here's how we handle copyright matters.
        </p>

        <div style={{
          marginTop: 24, display: "flex", gap: 16, flexWrap: "wrap",
          fontSize: 13, color: C.textMuted, fontWeight: 500,
        }}>
          <span>Effective: January 2024</span>
          <span style={{ color: C.border }}>·</span>
          <span>Governed by Irish Law</span>
        </div>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 100px" }}>
        <div style={{ display: "grid", gap: 12 }}>
          {sections.map((s) => (
            <div key={s.num} className="section-card" style={{
              background: C.bgCard,
              border: `1px solid ${C.border}`,
              borderRadius: 16, padding: "28px 28px",
              position: "relative", overflow: "hidden",
            }}>
              {/* Ghost number */}
              <div style={{
                position: "absolute", top: 12, right: 20,
                fontSize: 56, fontWeight: 800, fontFamily: "'Sora', sans-serif",
                color: isDark ? "rgba(255,255,255,0.03)" : "rgba(12,105,200,0.05)",
                lineHeight: 1, userSelect: "none",
              }}>{s.num}</div>

              <div style={{
                fontSize: 11, fontWeight: 700, color: C.accent,
                textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10,
              }}>{s.num}</div>

              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: 17, fontWeight: 700, color: C.text,
                marginBottom: 12, letterSpacing: -0.2,
              }}>{s.title}</h3>

              {s.body && (
                <p style={{
                  fontSize: 14.5, color: C.textSecondary, lineHeight: 1.7,
                  marginBottom: s.list ? 14 : 0,
                }}>
                  {s.body}
                </p>
              )}

              {s.list && (
                <div style={{ display: "grid", gap: 6, marginBottom: s.note ? 14 : 0 }}>
                  {s.list.map((item) => (
                    <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{
                        width: 5, height: 5, borderRadius: "50%", background: C.accent,
                        flexShrink: 0, marginTop: 8,
                      }} />
                      <span style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {s.note && (
                <div style={{
                  padding: "12px 14px", borderRadius: 10,
                  background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
                  fontSize: 13, color: C.textSecondary, lineHeight: 1.6, fontStyle: "italic",
                }}>{s.note}</div>
              )}
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: 40, padding: "24px 28px",
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          borderRadius: 16, fontSize: 14, color: C.textSecondary, lineHeight: 1.65,
        }}>
          <span style={{ fontWeight: 700, color: C.accent }}>Copyright concerns?</span>{" "}
          Contact our Designated Copyright Agent at{" "}
          <a href="mailto:info@snowsproam.com" style={{ color: C.accent, fontWeight: 600 }}>
            info@snowsproam.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default CopyrightPolicy;